import api from './api';

export type EducationWorkflowKind =
  | 'PARENT_PROFILE'
  | 'INSTITUTE_PROFILE'
  | 'STUDENT_REQUIREMENT'
  | 'DEMO_CLASS'
  | 'TUTOR_VERIFICATION'
  | 'REFUND_REQUEST'
  | 'HOMEWORK_TASK'
  | 'COMPLAINT'
  | 'REVIEW_MODERATION'
  | 'STAFF_PERMISSION'
  | 'ANALYTICS_EVENT'
  | 'REMINDER_JOB'
  | 'PAYMENT_RECONCILIATION'
  | 'SUBSCRIPTION_USAGE';

export interface EducationWorkflowRecord {
  id?: string;
  _id?: string | { $oid?: string };
  kind: EducationWorkflowKind;
  status: string;
  title?: string;
  description?: string;
  ownerUserId?: string;
  studentUserId?: string;
  parentUserId?: string;
  teacherUserId?: string;
  instituteUserId?: string;
  assignedToUserId?: string;
  relatedRecordId?: string;
  idempotencyKey?: string;
  receiptNumber?: string;
  scheduledAt?: string;
  dueAt?: string;
  completedAt?: string;
  payload?: Record<string, any>;
  history?: Array<{
    action: string;
    actorUserId: string;
    status?: string;
    note?: string;
    payload?: Record<string, any>;
    createdAt: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface EducationRecordPayload {
  status?: string;
  title?: string;
  description?: string;
  ownerUserId?: string;
  studentUserId?: string;
  parentUserId?: string;
  teacherUserId?: string;
  instituteUserId?: string;
  assignedToUserId?: string;
  relatedRecordId?: string;
  idempotencyKey?: string;
  scheduledAt?: string;
  dueAt?: string;
  payload?: Record<string, any>;
  note?: string;
  action?: string;
}

export const getEducationRecordId = (record: EducationWorkflowRecord) => {
  if (record.id) return record.id;
  if (typeof record._id === 'string') return record._id;
  return record._id?.$oid || '';
};

export const educationOpsService = {
  summary: async () => {
    const response = await api.get('/education-ops/summary');
    return response.data;
  },

  listRecords: async (params?: { kind?: EducationWorkflowKind; status?: string; includeArchived?: boolean }) => {
    const response = await api.get<EducationWorkflowRecord[]>('/education-ops/records', {
      params: {
        ...params,
        includeArchived: params?.includeArchived ? 'true' : undefined,
      },
    });
    return response.data;
  },

  createRecord: async (kind: EducationWorkflowKind, payload: EducationRecordPayload) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/records/${kind}`, payload);
    return response.data;
  },

  updateRecord: async (id: string, payload: EducationRecordPayload) => {
    const response = await api.patch<EducationWorkflowRecord>(`/education-ops/records/${id}`, payload);
    return response.data;
  },

  updateDemoStatus: async (id: string, status: string, payload?: Record<string, any>, scheduledAt?: string, note?: string) => {
    const response = await api.patch<EducationWorkflowRecord>(`/education-ops/demos/${id}/status`, {
      status,
      scheduledAt,
      payload,
      note,
    });
    return response.data;
  },

  recordDemoAttendance: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/demos/${id}/attendance`, payload);
    return response.data;
  },

  submitDemoFeedback: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/demos/${id}/feedback`, payload);
    return response.data;
  },

  convertDemoToPaid: async (id: string, payload?: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/demos/${id}/convert`, payload || {});
    return response.data;
  },

  updateComplaintStatus: async (id: string, payload: Record<string, any>) => {
    const response = await api.patch<EducationWorkflowRecord>(`/education-ops/complaints/${id}/status`, payload);
    return response.data;
  },

  addComplaintEvidence: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/complaints/${id}/evidence`, payload);
    return response.data;
  },

  appealComplaint: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/complaints/${id}/appeal`, payload);
    return response.data;
  },

  moderateReview: async (id: string, payload: Record<string, any>) => {
    const response = await api.patch<EducationWorkflowRecord>(`/education-ops/reviews/${id}/moderate`, payload);
    return response.data;
  },

  getParentProfile: async () => {
    const response = await api.get<EducationWorkflowRecord[]>('/education-ops/parent/profile');
    return response.data;
  },

  saveParentProfile: async (payload: EducationRecordPayload) => {
    const response = await api.post<EducationWorkflowRecord>('/education-ops/parent/profile', payload);
    return response.data;
  },

  getInstituteProfile: async () => {
    const response = await api.get<EducationWorkflowRecord[]>('/education-ops/institutes/profile');
    return response.data;
  },

  saveInstituteProfile: async (payload: EducationRecordPayload) => {
    const response = await api.post<EducationWorkflowRecord>('/education-ops/institutes/profile', payload);
    return response.data;
  },

  createPaymentWebhookCheck: async (payload: EducationRecordPayload) => {
    const response = await api.post<EducationWorkflowRecord>('/education-ops/payments/webhook-check', payload);
    return response.data;
  },

  consumeSubscriptionUsage: async (id: string, metric: string, increment = 1) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/subscriptions/usage/${id}/consume`, {
      metric,
      increment,
    });
    return response.data;
  },

  submitHomework: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/homework/${id}/submit`, payload);
    return response.data;
  },

  reviewHomework: async (id: string, payload: Record<string, any>) => {
    const response = await api.post<EducationWorkflowRecord>(`/education-ops/homework/${id}/review`, payload);
    return response.data;
  },

  auditLogs: async (targetRecordId?: string) => {
    const response = await api.get('/education-ops/audit-logs', {
      params: { targetRecordId },
    });
    return response.data;
  },
};

export default educationOpsService;
